import React from 'react';
import { Search, Globe, LogOut, Menu } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../hooks/useAuth';
import { logout } from '../api/auth';

interface NavbarProps {
    onMenuClick: () => void;
}

export default function Navbar({ onMenuClick }: NavbarProps) {
    const { language, setLanguage, t } = useLanguage();
    const { state, dispatch } = useAuth();
    const { user } = state;

    const handleLogout = async () => {
        await logout();
        dispatch({ type: 'LOGOUT' });
    };

    const getUserInitials = () => {
        if (user?.name) {
            return user.name.split(' ').map(n => n[0]).join('');
        } else if (user?.email) {
            return user.email[0].toUpperCase();
        }
        return 'U'; // U for User
    }

    return (
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
                {/* Hamburger Menu Button - visible on mobile */}
                <button onClick={onMenuClick} className="md:hidden p-1 mr-2 text-gray-600 hover:text-gray-900">
                    <Menu className="h-6 w-6" />
                </button>

                {/* Search Bar */}
                <div className="flex-1 relative max-w-lg">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder={t('common.search')}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                </div>

                {/* Right-side Icons & User Info */}
                <div className="ml-4 flex items-center space-x-2 sm:space-x-4">
                    <div className="relative flex items-center">
                        <Globe className="w-5 h-5 text-gray-500 mr-1 sm:mr-2" />
                        <select
                            value={language}
                            onChange={(e) => setLanguage(e.target.value as 'en' | 'fr')}
                            className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium cursor-pointer text-sm sm:text-base"
                        >
                            <option value="en">EN</option>
                            <option value="fr">FR</option>
                        </select>
                    </div>

                    {state.isAuthenticated && (
                        <div className="flex items-center">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#2E1C87] rounded-full flex items-center justify-center text-white font-semibold mr-2 sm:mr-3">
                                {getUserInitials()}
                            </div>
                            <div className="hidden sm:block">
                                <span className="text-gray-700 font-medium">{user?.name || user?.email || 'User'}</span>
                                {user?.roles && <span className="text-gray-500 text-sm block">{user.roles.join(', ')}</span>}
                            </div>
                        </div>
                    )}

                    <button 
                        onClick={handleLogout} 
                        className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        title="Logout"
                    >
                        <LogOut className="w-5 h-5" />
                    </button>
                </div>
            </div>
        </header>
    );
}
